import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Plus, Minus, Trash2, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import EyeCatchingHeading from './EyeCatchingHeading';
import { getCachedShippingCost, setCachedShippingCost, hasValidCache } from '../../utils/shippingCostCache';

const SlideOutCart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const cartContext = useCart();
  const { cartItems = [], updateQuantity, removeFromCart, clearCart, getTotalPrice, getTotalItems } = cartContext || {};
  const [isMobile, setIsMobile] = useState(false);
  
  // Initialize with cached shipping cost (with expiration check)
  // Default to null if no cache exists - will be set when component opens
  const [shippingCost, setShippingCost] = useState(() => {
    const cached = getCachedShippingCost();
    return cached !== null && cached !== undefined ? cached : null;
  });
  
  // Fetch shipping cost from backend only if cache is missing or expired
  // The backend stores this value in the database (updated monthly on the 1st)
  // Backend reads from database, not Printful API - so it's fast and doesn't call Printful
  useEffect(() => {
    const fetchShippingCost = async () => {
      try {
        const subtotal = getTotalPrice ? getTotalPrice() : 0;
        const FREE_SHIPPING_THRESHOLD = 200; // AUD
        
        // Free shipping if subtotal >= $200
        if (subtotal >= FREE_SHIPPING_THRESHOLD) {
          setShippingCost(0);
          return;
        }
        
        // Check if we have a valid cache first (saves API calls)
        if (hasValidCache()) {
          const cached = getCachedShippingCost();
          if (cached !== null && cached !== undefined) {
            setShippingCost(cached);
            return; // Use cached value, no API call needed
          }
        }
        
        // Only fetch if cache is missing or expired
        // Use simpler endpoint that just returns the stored shipping cost
        const isProduction = typeof window !== 'undefined' && 
          (window.location.hostname.includes('rainbow-sails.shop') ||
           (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
        const apiBase = process.env.REACT_APP_API_URL || 
          (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
        const baseUrl = apiBase.replace('/api/v1', '');
        const response = await fetch(`${baseUrl}/api/orders/get-shipping-cost`);
        if (response.ok) {
          const data = await response.json();
          if (data.shippingCost !== null && data.shippingCost !== undefined) {
            setShippingCost(data.shippingCost);
            // Cache the value for 30 days
            setCachedShippingCost(data.shippingCost);
          } else {
            // Use cached value if API doesn't return one
            const cached = getCachedShippingCost();
            if (cached !== null && cached !== undefined) setShippingCost(cached);
          }
        } else {
          // Use cached value on API error
          const cached = getCachedShippingCost();
          if (cached !== null && cached !== undefined) setShippingCost(cached);
        }
      } catch (error) {
        console.error('Error fetching shipping cost:', error);
        // Use cached value on error
        const cached = getCachedShippingCost();
        if (cached !== null && cached !== undefined) setShippingCost(cached);
      }
    };
    
    if (isOpen && cartItems && cartItems.length > 0) {
      fetchShippingCost();
    }
  }, [isOpen, cartItems, getTotalPrice]);
  
  // Handle responsive breakpoint
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      logger.logComponentMount('SlideOutCart', {
        itemCount: cartItems?.length || 0,
        totalPrice: getTotalPrice ? getTotalPrice() : 0
      });
      // Track cart view for analytics
      analyticsService.trackCartView(cartItems || [], getTotalPrice ? getTotalPrice() : 0);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, cartItems, getTotalPrice]);

  const handleCheckout = (e) => {
    const timestamp = new Date().toISOString();
    const buttonId = 'CHECKOUT_BUTTON';
    
    console.log('\n' + '='.repeat(80));
    console.log(`🔘 [BUTTON PRESS] ${buttonId} - Checkout Button Clicked`);
    console.log(`⏰ [TIMESTAMP] ${timestamp}`);
    console.log(`📍 [LOCATION] SlideOutCart.js:handleCheckout`);
    console.log('='.repeat(80));
    
    console.log(`📊 [EVENT DATA] Event object:`, e);
    console.log(`📊 [EVENT DATA] Event target:`, e?.target);
    console.log(`📊 [EVENT DATA] Event currentTarget:`, e?.currentTarget);
    console.log(`📊 [EVENT DATA] Event type:`, e?.type);
    console.log(`📊 [STATE] Cart isOpen:`, isOpen);
    console.log(`📊 [STATE] Cart items count:`, cartItems?.length || 0);
    console.log(`📊 [STATE] Total price:`, getTotalPrice ? getTotalPrice() : 0);
    
    // Stop event propagation to prevent triggering other handlers
    if (e) {
      console.log(`🛑 [ACTION] Stopping event propagation`);
      e.preventDefault();
      e.stopPropagation();
      console.log(`✅ [RESULT] Event propagation stopped`);
    }
    
    logger.logButtonClick('Cart Checkout', {
      itemCount: cartItems?.length || 0,
      totalPrice: getTotalPrice ? getTotalPrice() : 0
    });
    logger.logCartAction('checkout_initiated', null, null, {
      itemCount: cartItems?.length || 0,
      totalPrice: getTotalPrice ? getTotalPrice() : 0
    });
    
    console.log(`🔄 [FUNCTION CALL] Calling onClose() to close cart`);
    console.log(`📤 [DATA FLOW] onClose() → App.js → setIsCartOpen(false)`);
    onClose();
    console.log(`✅ [RESULT] onClose() completed`);
    
    console.log(`🧭 [NAVIGATION] Navigating to /checkout`);
    console.log(`📤 [DATA FLOW] navigate('/checkout') → React Router → CustomCheckout component`);
    navigate('/checkout');
    console.log(`✅ [RESULT] Navigation initiated`);
    
    console.log('='.repeat(80) + '\n');
  };

  const handleQuantityUpdate = (itemId, newQuantity, itemName) => {
    logger.logCartAction('quantity_update', itemId, itemName, { newQuantity });
    updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId, itemName) => {
    logger.logCartAction('remove_item', itemId, itemName);
    removeFromCart(itemId);
  };

  const handleClearCart = () => {
    logger.logButtonClick('Clear Cart', { itemCount: cartItems?.length || 0 });
    logger.logCartAction('clear_cart', null, null, { itemCount: cartItems?.length || 0 });
    clearCart();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={(e) => {
              console.log('🛒 [CART DEBUG] Backdrop clicked');
              console.log('🛒 [CART DEBUG] Event target:', e.target);
              console.log('🛒 [CART DEBUG] Cart isOpen state:', isOpen);
              e.stopPropagation();
              onClose();
            }}
          />
          
          {/* Cart Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: isMobile ? '100vw' : '400px', 
              maxWidth: '100vw',
              height: '100vh', 
              position: 'fixed',
              right: 0,
              top: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(31, 41, 55, 0.95))',
              backdropFilter: 'blur(20px)',
              borderLeft: isMobile ? 'none' : '2px solid rgba(6, 182, 212, 0.3)',
              boxShadow: '0 0 50px rgba(0, 0, 0, 0.8), 0 0 100px rgba(6, 182, 212, 0.2)',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxSizing: 'border-box'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '24px',
              borderBottom: '2px solid rgba(6, 182, 212, 0.3)',
              background: 'rgba(0, 0, 0, 0.6)',
              width: '100%',
              boxSizing: 'border-box',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ShoppingBag style={{ width: '24px', height: '24px', color: '#ec4899' }} />
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  margin: 0,
                  background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}>
                  Cart ({getTotalItems ? getTotalItems() : 0})
                </h2>
              </div>
              <button
                onClick={onClose}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            {/* Cart Items */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              padding: '0px 24px 24px 24px',
              minHeight: 0,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {!cartItems || cartItems.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  padding: '48px 0'
                }}>
                  <ShoppingBag style={{ 
                    width: '80px', 
                    height: '80px', 
                    color: '#6b7280', 
                    marginBottom: '24px' 
                  }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#9ca3af',
                    marginBottom: '12px',
                    margin: 0
                  }}>
                    Your cart is empty
                  </h3>
                  <p style={{
                    color: '#6b7280',
                    fontSize: '16px',
                    margin: 0
                  }}>
                    Add some consciousness-expanding art to get started!
                  </p>
                </div>
              ) : (
                <div>
                  {/* Your Style Heading */}
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    marginBottom: '16px',
                    textAlign: 'center',
                    color: 'white'
                  }}>
                    Your{' '}
                    <span style={{
                      background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4, #10b981, #f59e0b, #ec4899)',
                      backgroundSize: '300% 300%',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      animation: 'gradientShift 3s ease-in-out infinite'
                    }}>
                      Style
                    </span>
                  </h3>
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {cartItems.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        marginBottom: '16px',
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Image and Content Row */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '16px',
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                      }}>
                        {/* Product Image */}
                        <img
                          src={item.image || item.src || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iIzM3NDE1MSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='}
                          alt={item.title}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iIzM3NDE1MSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                        
                        {/* Stacked Content */}
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          {/* Product Title */}
                          <EyeCatchingHeading 
                            title={item.title} 
                            styleType={item.id ? `${item.id}-${item.title}` : item.title}
                            className="text-sm"
                          />
                          
                          {/* Price */}
                          <span style={{
                            color: '#10b981',
                            fontSize: '18px',
                            fontWeight: 'bold'
                          }}>
                            ${item.price}
                          </span>
                          
                          {/* Controls Row: - number + bin */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px'
                          }}>
                        <button
                          onClick={() => handleQuantityUpdate(item.id, item.quantity - 1, item.title)}
                          disabled={item.quantity <= 1}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#ef4444',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            opacity: item.quantity <= 1 ? 0.5 : 1
                          }}
                        >
                          <Minus style={{ width: '16px', height: '16px' }} />
                        </button>
                        
                        <span style={{
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          minWidth: '30px',
                          textAlign: 'center'
                        }}>
                          {item.quantity}
                        </span>
                        
                        <button
                          onClick={() => handleQuantityUpdate(item.id, item.quantity + 1, item.title)}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: 'rgba(16, 185, 129, 0.2)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#10b981',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Plus style={{ width: '16px', height: '16px' }} />
                        </button>
                        
                        <button
                          onClick={() => handleRemoveItem(item.id, item.title)}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: 'rgba(156, 163, 175, 0.2)',
                            border: '1px solid rgba(156, 163, 175, 0.3)',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Trash2 style={{ width: '16px', height: '16px' }} />
                        </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div style={{
                borderTop: '2px solid rgba(6, 182, 212, 0.3)',
                padding: '24px',
                background: 'rgba(0, 0, 0, 0.6)',
                width: '100%',
                boxSizing: 'border-box',
                flexShrink: 0
              }}>
                {/* Total */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <span style={{
                    color: '#10b981',
                    fontSize: '28px',
                    fontWeight: 'bold'
                  }}>
                    Total: ${getTotalPrice ? getTotalPrice().toFixed(2) : '0.00'}
                  </span>
                </div>
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={(e) => handleCheckout(e)}
                    disabled={false}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      fontWeight: 'bold',
                      padding: '16px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '16px',
                      opacity: 1
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #059669, #047857)';
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #10b981, #059669)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    <CreditCard style={{ width: '20px', height: '20px' }} />
                    Checkout
                  </button>
                  
                  <button
                    onClick={handleClearCart}
                    style={{
                      width: '100%',
                      background: 'rgba(156, 163, 175, 0.2)',
                      border: '1px solid rgba(156, 163, 175, 0.3)',
                      color: '#9ca3af',
                      fontWeight: '500',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(156, 163, 175, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(156, 163, 175, 0.2)';
                    }}
                  >
                    Clear Cart
                  </button>
                </div>
                
                {/* Shipping Info */}
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  {getTotalPrice() >= 200 ? (
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#10b981', 
                      fontWeight: 'bold',
                      margin: 0 
                    }}>
                      ✨ FREE SHIPPING UNLOCKED!
                    </p>
                  ) : (
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af',
                      margin: 0
                    }}>
                      ✨ Free shipping on orders over <span style={{ color: '#10b981', fontWeight: 'bold' }}>$200</span>
                    </p>
                  )}
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#9ca3af',
                    margin: '4px 0 0 0'
                  }}>
                    🚀 Worldwide shipping: <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>{shippingCost !== null && shippingCost !== undefined ? `$${shippingCost.toFixed(2)}` : 'included'}</span>
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SlideOutCart;